import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { useIntroducingBrokerById } from '@pages/IntroducingBroker/hooks/useIntroducingBrokerById'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useIBCampaignsResultsReadContext } from '@context/IBCampaignsResultsContext'

import { BriefcaseIcon } from '@icons/BriefcaseIcon'

import { isTickmillPartner } from '@utils/companyName.utils'

import { TickmillProductType } from '@model/TickmillProductType'

import { MenuSection } from '../useMenuSections'

export const useIBMenu = (): MenuSection => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { ibCampaignsResults } = useIBCampaignsResultsReadContext()
  const { data: introducingBroker } = useIntroducingBrokerById()
  const { isIBVisible, isMultiTierVisible } = useIBRoutesConditions()

  return useMemo(() => {
    const baseMenuItem: MenuSection = {
      id: 'introducing-broker',
      label: isTickmillPartner(account) ? t('Tabs.Partners') : t('Tabs.Introducing broker'),
      icon: BriefcaseIcon,
      condition: isIBVisible,
      products: [TickmillProductType.CFD],
    }

    if (!account?.clientIntroducingBroker?.id) {
      return {
        ...baseMenuItem,
        path: '/dashboard/introducing-broker/register/type',
      }
    }

    return {
      ...baseMenuItem,
      children: [
        {
          id: 'ib-dashboard',
          label: t('Tabs.Dashboard'),
          path: '/dashboard/introducing-broker/dashboard',
        },
        {
          id: 'ib-rebates',
          label: t('Tabs.Rebates'),
          children: [
            {
              id: 'ib-rebates-clients',
              label: t('IB.RebatesPage.Clients'),
              path: '/dashboard/introducing-broker/rebates/clients',
            },
            {
              id: 'ib-rebates-approvals',
              label: t('IB.RebatesPage.Approvals'),
              path: '/dashboard/introducing-broker/rebates/approvals',
            },
            {
              id: 'ib-rebates-groups',
              label: t('IB.RebatesPage.Groups'),
              path: '/dashboard/introducing-broker/rebates/groups',
            },
            {
              id: 'ib-rebates-transactions',
              label: t('IB.RebatesPage.Transactions'),
              path: '/dashboard/introducing-broker/rebates/transactions',
            },
          ],
        },
        {
          id: 'ib-wallets',
          label: t('Tabs.Wallets'),
          path: '/dashboard/introducing-broker/wallets',
        },
        {
          id: 'ib-clients',
          label: t('Tabs.Clients'),
          path: '/dashboard/introducing-broker/clients',
        },
        {
          id: 'ib-income',
          label: t('Tabs.Income'),
          path: '/dashboard/introducing-broker/income',
        },
        {
          id: 'ib-reports',
          label: t('Tabs.Reports'),
          path: '/dashboard/introducing-broker/reports',
        },
        {
          id: 'ib-transaction-history',
          label: t('Tabs.Transaction History'),
          path: '/dashboard/introducing-broker/transaction-history',
        },
        {
          id: 'ib-legal-documents',
          label: t('Tabs.Legal Documents'),
          path: '/dashboard/introducing-broker/legal-documents',
        },
        {
          id: 'ib-multi-tier',
          label: t('Tabs.Multi-Tier'),
          path: '/dashboard/introducing-broker/multi-tier',
          condition: isMultiTierVisible,
        },
        {
          id: 'ib-referral-materials',
          label: t('Tabs.Referral Materials'),
          path: '/dashboard/introducing-broker/referral-materials',
          condition: !isTickmillPartner(account),
          children: [
            {
              id: 'ib-referral-materials-links',
              label: t('Links'),
              path: '/dashboard/introducing-broker/referral-materials/links',
            },
            {
              id: 'ib-referral-materials-logos',
              label: t('Referral Materials.Logo'),
              path: '/dashboard/introducing-broker/referral-materials/logos',
            },
            {
              id: 'ib-referral-materials-banners',
              label: t('Referral Materials.Banners'),
              path: '/dashboard/introducing-broker/referral-materials/banners',
            },
            {
              id: 'ib-referral-materials-qr-code',
              label: t('Referral Materials.QR Code'),
              path: '/dashboard/introducing-broker/referral-materials/qr-code',
            },
          ],
        },
        {
          id: 'ib-contest',
          label: t('Tabs.IB Contest'),
          path: '/dashboard/introducing-broker/ib-contest',
          condition: !!ibCampaignsResults.length,
        },
        {
          id: 'ib-loyalty-program',
          label: t('Tabs.Loyalty Program'),
          condition: introducingBroker?.loyaltyProgramEnabled,
          path: '/dashboard/introducing-broker/loyalty-program',
        },
      ],
    }
  }, [
    account,
    ibCampaignsResults.length,
    introducingBroker?.loyaltyProgramEnabled,
    isIBVisible,
    isMultiTierVisible,
    t,
  ])
}
